import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import { Button } from "../components/ui/button";
import {
  LayoutDashboard,
  Users,
  Receipt,
  Wallet,
  UserCog,
  LogOut,
  Menu,
  Wand2,
  AlertCircle,
  PiggyBank,
} from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/record-payment", label: "Record Payment", icon: Wand2, testid: "nav-record-payment" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/outstanding", label: "Outstanding", icon: AlertCircle, testid: "nav-outstanding" },
  { to: "/billing", label: "Billing", icon: Receipt, testid: "nav-billing" },
  { to: "/payments", label: "Payments", icon: Wallet, testid: "nav-payments" },
  { to: "/expenses", label: "Expenses", icon: PiggyBank, adminOnly: true, testid: "nav-expenses" },
  { to: "/staff", label: "Staff", icon: UserCog, adminOnly: true, testid: "nav-staff" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [openMobile, setOpenMobile] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const links = navItems.filter((n) => !n.adminOnly || user?.role === "admin");

  return (
    <div className="min-h-screen flex bg-[#f8f9fa]">
      {/* Sidebar - desktop */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-gray-200 bg-white flex-col">
        <div className="px-5 py-5 border-b border-gray-200 flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-red-600 text-white grid place-items-center font-display font-bold">PDS</div>
          <div>
            <div className="text-xs uppercase tracking-wider text-red-700 font-semibold">Parameshwari</div>
            <div className="text-sm font-display font-semibold text-gray-900 leading-none">Data Services</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {links.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) => `pds-sidebar-link ${isActive ? "active" : ""}`}
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-gray-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-gray-50 text-left">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-red-600 text-white text-xs">
                    {user?.name?.[0]?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-xs text-gray-500 capitalize">{user?.role}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-menu-item" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-red-600 text-white grid place-items-center font-display font-bold text-xs">PDS</div>
          <div className="font-display font-semibold text-sm">Parameshwari</div>
        </div>
        <Button
          data-testid="mobile-menu-btn"
          variant="ghost"
          size="icon"
          onClick={() => setOpenMobile((s) => !s)}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>
      {openMobile && (
        <div className="md:hidden fixed top-14 left-0 right-0 z-30 bg-white border-b border-gray-200 p-3 space-y-1">
          {links.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpenMobile(false)}
              className={({ isActive }) => `pds-sidebar-link ${isActive ? "active" : ""}`}
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
          <button
            data-testid="mobile-logout-btn"
            onClick={handleLogout}
            className="pds-sidebar-link w-full"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 md:pl-0 pt-14 md:pt-0 min-w-0">
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
